import { Reservation } from '../types/reservation';
import { Vehicle } from '../types/vehicle';
import { Fleet } from '../types';
import { format } from 'date-fns';

/**
 * Service pour générer des fichiers ICS (iCalendar) pour les réservations
 * Format: RFC 5545 - Internet Calendaring and Scheduling Core Object Specification
 */

export const icsService = {
  /**
   * Génère un fichier ICS pour une réservation et déclenche le téléchargement
   */
  generateICSFile(
    reservation: Reservation,
    vehicle: Vehicle,
    fleet: Fleet
  ): void {
    const icsContent = this.createICSContent(reservation, vehicle, fleet);
    this.downloadICS(icsContent, `reservation-${vehicle.licensePlate}.ics`);
  },

  /**
   * Crée le contenu ICS selon le format RFC 5545
   */
  createICSContent(
    reservation: Reservation,
    vehicle: Vehicle,
    fleet: Fleet
  ): string {
    const startDate = reservation.startDate.toDate();
    const endDate = reservation.endDate.toDate();

    // Formater les dates au format iCalendar (YYYYMMDDTHHMMSS)
    const formatICSDate = (date: Date): string => {
      return format(date, "yyyyMMdd'T'HHmmss");
    };

    // Générer un UID unique pour l'événement
    const uid = `reservation-${reservation.id}@verifleet.app`;

    // Créer le timestamp de création
    const now = new Date();
    const dtstamp = formatICSDate(now);

    // Construire le titre de l'événement
    const summary = `Réservation véhicule - ${vehicle.make} ${vehicle.model}`;

    // Construire la description
    const description = [
      `Motif: ${reservation.purpose}`,
      `Véhicule: ${vehicle.make} ${vehicle.model} (${vehicle.licensePlate})`,
      `Flotte: ${fleet.name}`,
      reservation.notes ? `Notes: ${reservation.notes}` : '',
    ].filter(Boolean).join('\\n');

    // Localisation (optionnel)
    const location = fleet.name;

    // Construire le contenu ICS
    const icsLines = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//VeriFleet//Reservation//FR',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTAMP:${dtstamp}`,
      `DTSTART:${formatICSDate(startDate)}`,
      `DTEND:${formatICSDate(endDate)}`,
      `SUMMARY:${summary}`,
      `DESCRIPTION:${description}`,
      `LOCATION:${location}`,
      `STATUS:${reservation.status.toUpperCase()}`,
      'BEGIN:VALARM',
      'TRIGGER:-PT30M', // Rappel 30 minutes avant
      'DESCRIPTION:Rappel de réservation',
      'ACTION:DISPLAY',
      'END:VALARM',
      'END:VEVENT',
      'END:VCALENDAR',
    ];

    return icsLines.join('\r\n');
  },

  /**
   * Déclenche le téléchargement du fichier ICS
   */
  downloadICS(content: string, filename: string): void {
    const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;

    // Déclencher le téléchargement
    document.body.appendChild(link);
    link.click();

    // Nettoyer
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  /**
   * Crée un lien mailto pour partager la réservation par email
   */
  createEmailShareLink(
    reservation: Reservation,
    vehicle: Vehicle,
    fleet: Fleet
  ): string {
    const subject = encodeURIComponent(`Réservation véhicule - ${vehicle.make} ${vehicle.model}`);
    const body = encodeURIComponent([
      `Bonjour,`,
      ``,
      `Voici les détails de votre réservation :`,
      ``,
      `Véhicule : ${vehicle.make} ${vehicle.model} (${vehicle.licensePlate})`,
      `Flotte : ${fleet.name}`,
      `Motif : ${reservation.purpose}`,
      `Début : ${format(reservation.startDate.toDate(), 'dd/MM/yyyy HH:mm')}`,
      `Fin : ${format(reservation.endDate.toDate(), 'dd/MM/yyyy HH:mm')}`,
      reservation.notes ? `Notes : ${reservation.notes}` : '',
      ``,
      `Cordialement,`,
      `L'équipe VeriFleet`,
    ].filter(Boolean).join('\n'));

    return `mailto:?subject=${subject}&body=${body}`;
  },
};

export default icsService;
